<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useFilteredJobStore } from '@/components/jobs/job-search-page/filteredJobStore'
import { storeToRefs } from 'pinia'
import { useStore } from 'vuex-composition-helpers'
import draggable from 'vuedraggable'
import JobFilterSetContextMenu from '@/components/jobs/job-search-page/JobFilterSetContextMenu.vue'
import VsTextInput from '@/components/vision/Inputs/VsTextInput.vue'
import { JobFilterSet } from '@ironsight/types'
import { useJobFilterSets } from '@/components/jobs/job-search-page/useJobFilterSets'
import { useJobFilterSetsUi } from '@/components/jobs/job-search-page/useJobFilterSetsUi'

const nameInputElement = ref<InstanceType<typeof VsTextInput> | null>(null)
const updateNameInputElement = ref<InstanceType<typeof VsTextInput> | null>(null)

const filteredJobStore = useFilteredJobStore()
const { getFilters, getFiltersWithoutBuckets, getSelectedFilterSetId } =
  storeToRefs(filteredJobStore)
const vuex = useStore()
const isGlobalAdmin = computed(() => vuex.getters['permissions/isGlobalAdmin'])
const currentUserSid = computed(() => vuex.getters['permissions/getContext']?.userSid ?? null)

const {
  orderedFilterSets,
  hasFilters,
  hasError,
  isFetching,
  saveButtonTooltip,
  isCreating,
  isUpdating,
  isDeleting,
  loadFilterSet,
  createFilterSet,
  updateFilterSet,
  deleteFilterSet,
  loadFilterSetAndMoveToFront,
  clearAllFilters,
  reorderVisibleFilterSets,
  validateFilterSetName,
  filtersEqual,
  LIMITS,
} = useJobFilterSets(currentUserSid, isGlobalAdmin, getFilters, {
  loadFilterSet: filteredJobStore.loadFilterSet,
  clearFilters: filteredJobStore.clearFilters,
  setSelectedFilterSetId: filteredJobStore.setSelectedFilterSetId,
})

const {
  TRUNCATION,
  ui,
  elements,
  updateMenuStyles,
  chipVisibility,
  hiddenFilterSets,
  visibleFilterSetModel,
  isDraggingVisibleFilterSets,
  suppressChipHover,
  canDragVisibleFilterSets,
  maxOverflowChipLabel,
  setVisibleChipRef,
  setMeasuredChipRef,
  truncateName,
  shouldShowTooltip,
  toggleMenu,
  syncMenuVisibility,
  handleVisibleFilterSetDragStart,
  handleVisibleFilterSetDragChange,
  handleVisibleFilterSetDragEnd,
  handleChipPointerMove,
  handleChipPointerLeave,
  handleFilterSetClick,
  openUpdateMenu,
  openUpdateMenuFromOverflow,
  closeMoreMenu,
  closeUpdateMenu,
  resetSaveMenu,
  resetUpdateMenu,
  setValidationError,
  setUpdateValidationError,
  setNewFilterSetName,
  setUpdateFilterSetName,
} = useJobFilterSetsUi({
  orderedFilterSets,
  nameInputElement,
  updateNameInputElement,
  isFetching,
  isCreating,
  isUpdating,
  isDeleting,
  loadFilterSet,
  reorderVisibleFilterSets,
})

const handleSaveFilterSet = async () => {
  const result = await createFilterSet(ui.newFilterSetName)

  if (result.success) {
    resetSaveMenu()
  } else {
    setValidationError(result.error || 'Failed to save filter set')
  }
}

const handleUpdateFilterSetSubmit = async () => {
  if (!ui.updateFilterSetId) return

  const filterSetId = ui.updateFilterSetId
  const filterSetName = ui.updateFilterSetName
  const validationError = validateFilterSetName(filterSetName, true)

  if (validationError) {
    setUpdateValidationError(validationError)
    return
  }

  resetUpdateMenu()

  const result = await updateFilterSet(filterSetId, filterSetName)

  if (!result.success) {
    const filterSet = orderedFilterSets.value.find((set) => set.id === filterSetId)

    if (filterSet) {
      openUpdateMenu(filterSet)
      setUpdateFilterSetName(filterSetName)
    }

    setUpdateValidationError(result.error || 'Failed to update filter set')
  }
}

const handleDeleteFilterSet = async (filterSet: JobFilterSet) => {
  await deleteFilterSet(filterSet.id)
  ui.openMenuId = null
  closeMoreMenu()
}

const handleLoadFilterSetFromOverflow = (filterSet: JobFilterSet) => {
  closeMoreMenu()
  loadFilterSetAndMoveToFront(filterSet)
}

watch(
  getFiltersWithoutBuckets,
  (currentFilters) => {
    // Clear selection if filters don't match selected set
    if (!getSelectedFilterSetId.value) return

    const selectedSet = orderedFilterSets.value.find(
      (set) => set.id === getSelectedFilterSetId.value,
    )
    if (selectedSet && !filtersEqual(currentFilters, selectedSet.data.filters)) {
      filteredJobStore.setSelectedFilterSetId(null)
    }
  },
  { deep: true },
)
</script>

<template>
  <div
    :ref="elements.containerRef"
    :class="{
      'filter-sets-bar--dragging': isDraggingVisibleFilterSets,
      'filter-sets-bar--suppress-hover': suppressChipHover,
    }"
    class="filter-sets-bar"
    data-test="filter-sets-bar"
    @mousemove="handleChipPointerMove"
    @mouseleave="handleChipPointerLeave"
  >
    <div aria-hidden="true" class="chip-measurements" data-test="filter-sets-measurements">
      <v-chip
        v-for="filterSet in orderedFilterSets"
        :key="`measure-${filterSet.id}`"
        :ref="(comp) => setMeasuredChipRef(filterSet.id, comp)"
        class="ma-2 ml-0 option"
        color="primary"
        flat
        shrink
      >
        {{ truncateName(filterSet.name, TRUNCATION.CHIP_NAME_LENGTH) }}
      </v-chip>
      <v-chip
        :ref="elements.moreChipMeasureRef"
        data-test="saved-filter-more-measure"
        class="ma-2 ml-0 option more-chip"
        color="primary"
        flat
        shrink
      >
        {{ maxOverflowChipLabel }}
      </v-chip>
    </div>

    <div :ref="elements.contentAreaRef" class="d-flex align-center">
      <div v-if="isFetching" class="mr-3">
        <v-progress-circular color="primary" indeterminate size="20" />
      </div>

      <div v-else>
        <v-chip
          v-if="orderedFilterSets.length > 0"
          :ref="elements.allJobsChipRef"
          data-test="saved-filter-default"
          :class="{ 'option--active': !hasFilters }"
          class="ma-2 ml-0 option"
          color="primary"
          flat
          shrink
          @click="clearAllFilters"
        >
          Default view
        </v-chip>

        <draggable
          v-model="visibleFilterSetModel"
          animation="150"
          class="filter-set-chip-list"
          draggable=".saved-filter-chip-item"
          ghost-class="filter-set-chip--ghost"
          tag="div"
          :delay="0"
          :disabled="!canDragVisibleFilterSets"
          @start="handleVisibleFilterSetDragStart"
          @change="handleVisibleFilterSetDragChange"
          @end="handleVisibleFilterSetDragEnd"
        >
          <div
            v-for="filterSet in visibleFilterSetModel"
            :key="filterSet.id"
            class="saved-filter-chip-item"
          >
            <v-tooltip
              :disabled="!shouldShowTooltip(filterSet.name, TRUNCATION.CHIP_NAME_LENGTH)"
              bottom
            >
              <template #activator="{ on, attrs }">
                <v-menu
                  :value="ui.openMenuId === filterSet.id"
                  nudge-bottom="4"
                  offset-y
                  rounded="xl"
                  @input="syncMenuVisibility(filterSet.id, $event)"
                >
                  <template #activator="{}">
                    <div class="saved-filter-chip-wrapper">
                      <v-chip
                        :ref="(comp) => setVisibleChipRef(filterSet.id, comp)"
                        data-test="saved-filter-chip"
                        :class="{
                          'option--active': getSelectedFilterSetId === filterSet.id,
                        }"
                        class="ma-2 ml-0 option"
                        color="primary"
                        flat
                        shrink
                        v-bind="attrs"
                        @click="handleFilterSetClick(filterSet)"
                        v-on="on"
                        @contextmenu.prevent="toggleMenu(filterSet.id)"
                      >
                        {{ truncateName(filterSet.name, TRUNCATION.CHIP_NAME_LENGTH) }}
                      </v-chip>
                    </div>
                  </template>
                  <job-filter-set-context-menu
                    :filter-set="filterSet"
                    :is-deleting="isDeleting"
                    :is-updating="isUpdating"
                    @delete="handleDeleteFilterSet"
                    @update="openUpdateMenu"
                  />
                </v-menu>
              </template>
              {{ filterSet.name }}
            </v-tooltip>
          </div>
        </draggable>

        <v-menu
          v-if="chipVisibility.showMore"
          v-model="ui.isMoreSetsMenuVisible"
          :close-on-content-click="false"
          min-width="200"
          offset-y
          rounded="xl"
        >
          <template #activator="{ on, attrs }">
            <v-chip
              :ref="elements.moreChipRef"
              data-test="saved-filter-more"
              class="ma-2 ml-0 option more-chip"
              color="primary"
              flat
              shrink
              v-bind="attrs"
              v-on="on"
            >
              +{{ hiddenFilterSets.length }} more...
            </v-chip>
          </template>
          <v-card class="more-menu-card">
            <v-list dense>
              <v-list-item
                v-for="filterSet in hiddenFilterSets"
                :key="filterSet.id"
                :class="{
                  'selected-item': getSelectedFilterSetId === filterSet.id,
                }"
                class="more-menu-item"
                @click="handleLoadFilterSetFromOverflow(filterSet)"
              >
                <v-list-item-content>
                  <v-tooltip
                    :disabled="!shouldShowTooltip(filterSet.name, TRUNCATION.MENU_NAME_LENGTH)"
                    bottom
                  >
                    <template #activator="{ on, attrs }">
                      <v-list-item-title v-bind="attrs" v-on="on">
                        {{ truncateName(filterSet.name, TRUNCATION.MENU_NAME_LENGTH) }}
                      </v-list-item-title>
                    </template>
                    {{ filterSet.name }}
                  </v-tooltip>
                </v-list-item-content>
                <v-list-item-action class="m-0 p-0">
                  <v-menu
                    :value="ui.openMenuId === filterSet.id"
                    offset-y
                    rounded="xl"
                    @input="syncMenuVisibility(filterSet.id, $event)"
                  >
                    <template #activator="{ on, attrs }">
                      <v-btn
                        class="more-icon-button"
                        icon
                        small
                        v-bind="attrs"
                        v-on="on"
                        @click.stop="toggleMenu(filterSet.id)"
                      >
                        <v-icon class="more-icon">more_horiz</v-icon>
                      </v-btn>
                    </template>
                    <job-filter-set-context-menu
                      :filter-set="filterSet"
                      :is-deleting="isDeleting"
                      :is-updating="isUpdating"
                      @delete="handleDeleteFilterSet"
                      @update="openUpdateMenuFromOverflow"
                    />
                  </v-menu>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card>
        </v-menu>
      </div>

      <v-tooltip :disabled="false" bottom>
        <template #activator="{ on: tooltipOn, attrs: tooltipAttrs }">
          <div v-bind="tooltipAttrs" v-on="tooltipOn">
            <v-menu
              v-model="ui.isSaveMenuVisible"
              :close-on-content-click="false"
              min-width="300"
              offset-y
              rounded="xl"
            >
              <template #activator="{ on, attrs }">
                <vs-button
                  :ref="elements.saveButtonRef"
                  :disabled="
                    !hasFilters ||
                    orderedFilterSets.length >= LIMITS.MAX_SAVED_SETS ||
                    isGlobalAdmin
                  "
                  :loading="isFetching || isCreating || isDeleting || isUpdating"
                  class="save-filter-button"
                  data-test="save-filter-sets-button"
                  icon="mdi-bookmark-plus-outline"
                  small
                  type="secondary"
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-card class="save-menu-card">
                <v-card-title class="save-menu-title pb-4">Create saved filter</v-card-title>
                <vs-text-input
                  ref="nameInputElement"
                  :error-messages="ui.validationError ? [ui.validationError] : []"
                  :value="ui.newFilterSetName"
                  autofocus
                  class="name-input"
                  label="Name"
                  @input="setNewFilterSetName"
                  @keydown.enter.prevent="handleSaveFilterSet"
                />
                <v-card-actions class="save-actions">
                  <v-spacer />
                  <vs-button
                    :loading="isCreating"
                    data-test="save-filter-set-button"
                    label="Save"
                    type="primary"
                    @click="handleSaveFilterSet"
                  />
                </v-card-actions>
              </v-card>
            </v-menu>
          </div>
        </template>
        {{ saveButtonTooltip }}
      </v-tooltip>
    </div>

    <div
      v-if="ui.isUpdateMenuVisible"
      :ref="elements.updateMenuRef"
      class="update-menu-floating"
      :style="updateMenuStyles"
      @keydown.esc.stop.prevent="closeUpdateMenu"
    >
      <v-card class="update-menu-card">
        <v-card-title class="update-menu-title pb-4">Rename saved filter</v-card-title>
        <vs-text-input
          ref="updateNameInputElement"
          :error-messages="ui.updateValidationError ? [ui.updateValidationError] : []"
          :value="ui.updateFilterSetName"
          autofocus
          class="name-input"
          label="Name"
          @input="setUpdateFilterSetName"
          @keydown.enter.prevent="handleUpdateFilterSetSubmit"
        />
        <v-card-actions class="update-actions">
          <v-spacer />
          <vs-button
            :loading="isUpdating"
            data-test="update-filter-set-button"
            label="Rename"
            type="primary"
            @click="handleUpdateFilterSetSubmit"
          />
        </v-card-actions>
      </v-card>
    </div>

    <vs-snackbar v-model="hasError" type="error">
      Something went wrong - please contact IronSight support
    </vs-snackbar>
  </div>
</template>

<style scoped>
.filter-sets-bar {
  padding: 12px 16px;
  background-color: white;
  width: 100%;
  min-width: 0;
  flex: 1;
  position: relative;
}

.chip-measurements {
  position: absolute;
  visibility: hidden;
  pointer-events: none;
  height: 0;
  overflow: hidden;
  white-space: nowrap;
}

.update-menu-floating {
  z-index: 8;
  min-width: 300px;
  max-width: min(300px, calc(100vw - 16px));
}

.filter-set-chip-list {
  display: inline-flex;
  align-items: center;
}

.saved-filter-chip-wrapper {
  display: inline-flex;
}

.save-menu-card,
.more-menu-card,
.update-menu-card {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-radius: 8px;
}

.selected-item {
  background-color: var(--color-blue--lightest) !important;
}

.more-menu-item {
  min-height: 40px !important;
  max-height: 40px !important;
  height: 40px !important;
}

.more-menu-card .v-list-item {
  min-height: 40px !important;
  max-height: 40px !important;
  height: 40px !important;
}

.save-menu-title,
.update-menu-title {
  font-size: 1.125rem !important;
  font-family: 'Montserrat', sans-serif !important;
  font-weight: 700 !important;
}

.name-input {
  margin-left: 16px !important;
  margin-right: 16px !important;
}

.save-actions,
.update-actions {
  padding: 16px !important;
}

.more-icon-button .more-icon {
  font-size: 24px !important;
}

.filter-set-chip--ghost {
  opacity: 0.65;
}
</style>

<style>
.filter-sets-bar .v-chip.option {
  height: 2.2rem !important;
  font-weight: 500 !important;
  font-family: var(--font-regular) !important;
  position: relative !important;
  color: var(--color-black) !important;
  border: thin solid var(--color-grey--lighter) !important;
  background-color: transparent !important;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
}

.filter-sets-bar .v-chip.option:hover {
  background-color: var(--color-blue--lighter) !important;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.filter-sets-bar--dragging .v-chip.option:hover,
.filter-sets-bar--dragging .v-chip.more-chip:hover,
.filter-sets-bar--suppress-hover .v-chip.option:hover,
.filter-sets-bar--suppress-hover .v-chip.more-chip:hover {
  background-color: transparent !important;
  transform: none;
  box-shadow: none;
}

.filter-sets-bar--dragging * {
  cursor: grabbing !important;
}

.filter-sets-bar .v-chip.option:active::before,
.filter-sets-bar .v-chip.option:focus::before {
  color: var(--color-blue) !important;
  background-color: var(--color-blue) !important;
  box-shadow: none;
}

.filter-sets-bar .v-chip.option--active {
  color: var(--color-blue) !important;
  background-color: var(--color-blue--lightest) !important;
}

.filter-sets-bar .v-chip.more-chip {
  font-style: italic;
  border-style: dashed !important;
}

.filter-sets-bar .v-chip.more-chip:hover {
  background-color: var(--color-grey--lighter) !important;
}

.filter-sets-bar .v-chip.option,
.filter-sets-bar .v-chip.more-chip {
  flex-shrink: 0 !important;
}
.save-filter-button {
  border-radius: 50% !important;
  width: 35px !important;
  height: 35px !important;
  min-width: 35px !important;
}

.job-filter-set-context-menu,
.v-menu__content:has(.job-filter-set-context-menu) {
  width: 240px !important;
  min-width: 240px !important;
  max-width: 240px !important;
}
</style>
